import { z } from "zod";
import { ulidSchema } from "./schemas.js";
import { fulfillmentStateSchemaV1 } from "./fulfillment.js";
const id = z.string().min(1).max(240);
const digest = z.string().regex(/^[a-f0-9]{64}$/u);
const timestamp = z.string().datetime({ offset: true });
/** Public immutable facts only. Object keys, addresses and provider identifiers never belong here. */
export const customerOrderedEditionSnapshotV1Schema = z
    .object({
    bookId: ulidSchema,
    previewId: id,
    editionLineageId: id,
    title: z.string().min(1).max(160).nullable(),
    titleEvidence: z.enum(["CAPTURED_AT_ORDER", "UNAVAILABLE_AT_CAPTURE", "UNAVAILABLE_LEGACY"]),
    packageId: id.nullable(),
    proofId: id.nullable(),
    planDigestSha256: digest.nullable(),
    approvalId: id.nullable(),
    approvedAt: timestamp.nullable(),
    sourceRevision: z.number().int().nonnegative(),
    manifestDigestSha256: digest,
    profileId: id,
    profileVersion: id,
    quantity: z.number().int().min(1).max(20),
    includedCopyQuantity: z.number().int().min(0).max(1),
    files: z
        .array(z
        .object({
        artifact: z.enum(["COVER", "INTERIOR"]),
        checksumSha256: digest,
        pageCount: z.number().int().positive().max(1000),
        exactVersionRetained: z.boolean(),
        ticketPath: z.string().max(1024).nullable(),
    })
        .strict())
        .max(2),
    capturedAt: timestamp,
})
    .strict();
export const customerBookOrderSummaryV1Schema = z
    .object({
    physicalOrderId: ulidSchema,
    bookId: ulidSchema,
    previewId: id,
    editionLineageId: id,
    quantity: z.number().int().min(1).max(20),
    includedCopyQuantity: z.number().int().min(0).max(1),
    orderRecordState: z.enum(["EVIDENCE_READY", "ACTIVE", "CANCELLED", "ARCHIVED"]),
    fulfillmentState: fulfillmentStateSchemaV1.nullable(),
    fulfillmentStateEvidence: z.enum(["DETAIL_LOOKUP_REQUIRED", "BOUNDED_SERVER_READ"]),
    createdAt: timestamp,
    updatedAt: timestamp,
})
    .strict();
export const customerBookOrderHistoryV1Schema = z
    .object({
    bookId: ulidSchema,
    orders: z.array(customerBookOrderSummaryV1Schema).max(50),
    nextCursor: z.string().min(1).max(4096).nullable(),
    coverage: z.literal("INDEXED_ORDERS_ONLY_LEGACY_ROWS_MAY_BE_UNAVAILABLE"),
})
    .strict();
export const customerOrderContextV1Schema = z
    .object({
    bookId: ulidSchema,
    physicalOrderId: ulidSchema.nullable(),
    previewId: id.nullable(),
    editionLineageId: id.nullable(),
    packageId: id.nullable(),
    proofId: id.nullable(),
    sourceRevision: z.number().int().nonnegative().nullable(),
    manifestDigestSha256: digest.nullable(),
})
    .strict();
export const customerOrderedFileTicketV1Schema = z
    .object({
    physicalOrderId: ulidSchema,
    artifact: z.enum(["COVER", "INTERIOR"]),
    checksumSha256: digest,
    downloadUrl: z
        .string()
        .url()
        .refine((value) => new URL(value).protocol === "https:"),
    expiresIn: z.number().int().min(1).max(300),
})
    .strict();
/**
 * Whether customers can order printed copies right now (redesign Stage 2, 2026-11-02): every page
 * reads it while it runs, so the owner opens printing without a new build. Only OPEN opens
 * ordering; anything the server can't confirm, or a page that can't ask, reads as closed.
 */
export const customerPrintingStateV1Schema = z
    .object({ printing: z.enum(["OPEN", "CLOSED"]) })
    .strict();
//# sourceMappingURL=book-fulfillment-customer.js.map